import { EntityList, EntityListProps, renderDate } from 'components/EntityList'
import _ from 'lodash'
import {
  ConstrainMode,
  IColumn,
  Icon,
  mergeStyles,
  SelectionMode,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { createFieldColumn } from 'views/helpers'

export enum View {
  inbox,
  outbox,
  sent,
  draft,
}

const iconClass = mergeStyles({
  fontSize: 20,
  height: 20,
  width: 20,
})

export type MailboxListProps = EntityListProps<{}> & { view: View }

export const MailboxList: React.FunctionComponent<MailboxListProps> = ({
  items,
  view,
  ...props
}) => {
  const { t } = useTranslation()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])

  const columnsInbox: IColumn[] = React.useMemo(
    () => [
      fieldColumn('isRead', {
        maxWidth: 10,
        isResizable: false,
        getGroupValue: (item) =>
          item.isRead ? 'Gelesene E-Mails' : 'Ungelesene E-Mails',
        onRender: (item) =>
          item.isRead ? (
            ''
          ) : (
            <Icon iconName="LocationDot" className={iconClass} />
          ),
      }),
      fieldColumn('from', {
        ungroupable: true,
        onRender: (item) => {
          if (item.isRead) {
            return item.from ? item.from.address : ''
          } else {
            return item.from ? <b>{item.from.address}</b> : ''
          }
        },
      }),
      fieldColumn('subject', {
        onRender: (item) => {
          return item.isRead ? item.subject : <b>{item.subject}</b>
        },
        ungroupable: true,
        isGrouped: true,
      }),
      fieldColumn('receivedAt', {
        onRender: (item, i, col) => {
          return item.isRead ? (
            renderDate(item, i, col)
          ) : (
            <b>{renderDate(item, i, col)}</b>
          )
        },
        ungroupable: true,
      }),
      fieldColumn('proving', {
        onRender: (item) => {
          if (item.isRead) {
            return item.proving ? item.proving.id : ''
          } else {
            return item.proving ? <b>{item.proving.id}</b> : ''
          }
        },
      }),
      fieldColumn('attachments', {
        onRender: (item) => {
          return item.attachments && item.attachments.length > 0 ? (
            <Icon iconName="attach" />
          ) : (
            ''
          )
        },
      }),
    ],
    [fieldColumn]
  )

  const columnsOutbox: IColumn[] = React.useMemo(
    () => [
      fieldColumn('isRead', {
        maxWidth: 10,
        isResizable: false,
        getGroupValue: (item) =>
          item.isRead ? 'Gelesene E-Mails' : 'Ungelesene E-Mails',
        onRender: (item) =>
          item.isRead ? (
            ''
          ) : (
            <Icon iconName="LocationDot" className={iconClass} />
          ),
      }),
      fieldColumn('to', {
        minWidth: 200,
        isResizable: true,
        ungroupable: true,
        onRender: (item) => {
          if (item.isRead) {
            return item.to
              ? _.join(
                  _.map(item.to, (x) => x.address),
                  ';'
                )
              : ''
          } else {
            return item.to ? (
              <b>
                {_.join(
                  _.map(item.to, (x) => x.address),
                  ';'
                )}
              </b>
            ) : (
              ''
            )
          }
        },
      }),
      fieldColumn('subject', {
        onRender: (item) => {
          return item.isRead ? item.subject : <b>{item.subject}</b>
        },
        isGrouped: true,
      }),
      fieldColumn('created', {
        onRender: (item, i, col) => {
          return item.unread ? (
            renderDate(item, i, col)
          ) : (
            <b>{renderDate(item, i, col)}</b>
          )
        },
        ungroupable: true,
      }),
      fieldColumn('proving', {
        onRender: (item) => {
          if (item.isRead) {
            return item.proving ? item.proving.id : ''
          } else {
            return item.proving ? <b>{item.proving.id}</b> : ''
          }
        },
      }),
      fieldColumn('attachments', {
        onRender: (item) => {
          return item.attachments && item.attachments.length > 0 ? (
            <Icon iconName="attach" />
          ) : (
            ''
          )
        },
      }),
    ],
    [fieldColumn]
  )

  const columnsSent: IColumn[] = React.useMemo(
    () => [
      fieldColumn('to', {
        minWidth: 200,
        isResizable: true,
        ungroupable: true,
        onRender: (item) => {
          return item.to
            ? _.join(
                _.map(item.to, (x) => x.address),
                ';'
              )
            : ''
        },
      }),
      fieldColumn('subject', {
        ungroupable: true,
        isGrouped: true,
      }),
      fieldColumn('sentAt', { onRender: renderDate, ungroupable: true }),
      fieldColumn('proving', {
        onRender: (item) => {
          return item.proving ? item.proving.id : ''
        },
      }),
      fieldColumn('attachments', {
        onRender: (item) => {
          return item.attachments && item.attachments.length > 0 ? (
            <Icon iconName="attach" />
          ) : (
            ''
          )
        },
      }),
    ],
    [fieldColumn]
  )

  const columnsDraft: IColumn[] = React.useMemo(
    () => [
      fieldColumn('to', {
        minWidth: 200,
        isResizable: true,
        ungroupable: true,
        onRender: (item) => {
          return item.to
            ? _.join(
                _.map(item.to, (x) => x.address),
                ';'
              )
            : ''
        },
      }),
      fieldColumn('subject', {
        ungroupable: true,
        isGrouped: true,
      }),
      fieldColumn('created', {
        ungroupable: true,
        onRender: renderDate,
        minWidth: 100,
        isSorted: true,
      }),
      fieldColumn('proving', {
        onRender: (item) => {
          return item.proving ? item.proving.id : ''
        },
      }),
      fieldColumn('attachments', {
        onRender: (item) => {
          return item.attachments && item.attachments.length > 0 ? (
            <Icon iconName="attach" />
          ) : (
            ''
          )
        },
      }),
    ],
    [fieldColumn]
  )

  return (
    <EntityList
      onShouldVirtualize={() => true}
      alternate={true}
      stickyHeader={true}
      selectionMode={SelectionMode.multiple}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={
        View.inbox === view
          ? columnsInbox
          : View.outbox === view
          ? columnsOutbox
          : View.sent === view
          ? columnsSent
          : View.draft === view
          ? columnsDraft
          : []
      }
      {...props}
    />
  )
}
